import React from 'react';
import { Button, ButtonGroup, Typography } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { FormSectionInfo } from '../../../form/components';
import { RHFRadioGroup } from '../../../../packages/react-hook-form/radio-group/radio-group';
import { RadioLabelled } from '../../../../packages/radio-labelled';
import { InputText } from '../../../input';
import { Form } from '../../../form';
import { BuyProcessFormUserTip } from '../user-tip/user-tip';
import { FormSectionContainer } from '../../../form/components/form-section-container/form-section-container';
import { InvoiceRecipient } from '../../../../global/entities/BuyOrder';
import { BuyProcessUtils } from '../../index';
import { Amount } from '../../../amount/amount';
import { BuyProcessFormNextButton } from '../buy-process-form-next-button/buy-process-form-next-button';
import { BuyProcessFormAdminSubmitButton } from '../buy-process-form-admin-submit-button/buy-process-form-admin-submit-button';
import { useCostStatement } from './use-cost-statement';
import { CostStatementReceiveLabel } from './cost-statement-utils';

const useStyles = makeStyles((theme) => ({
  sectionEnd: {
    marginBottom: theme.spacing(4),
  },
  subSectionEnd: {
    marginBottom: theme.spacing(2),
  },
  submitButton: {
    marginTop: theme.spacing(8),
  },
  section: {
    marginBottom: theme.spacing(5),
  },
}));

export const BuyProcessFormCostStatement = () => {
  const classes = useStyles();
  const {
    RECEIVER_EMAILS,
    RECEIVE_OPTION,
    receiveByEmail,
    onSubmit,
    formMethods,
    onBack,
    onForward,
    isUpdating,
  } = useCostStatement();

  const extraServicesCost = BuyProcessUtils.getExtraServicesCost();

  const renderMoney = (amount: number) => (
    <strong>
      <Amount locale="sv-SE" amount={amount} />
    </strong>
  );

  const renderBeneficialCost = (cost?: number) =>
    cost && (
      <>
        <span>
          Arvode samt Bolagsverkets avgift för registrering av verklig huvudman:{' '}
          {renderMoney(cost)}
        </span>
        <br />
      </>
    );

  const renderNameReviewCost = (cost?: number) =>
    cost && (
      <>
        <span>Arvode för namngranskning: {renderMoney(cost)}</span>
        <br />
      </>
    );

  const renderTaxHelpCost = (cost?: number) =>
    cost && (
      <>
        <span>
          Arvode för ansökan om f-skatt, moms och arbetsgivare:{' '}
          {renderMoney(cost)}
        </span>
        <br />
      </>
    );

  if (!extraServicesCost) {
    return null;
  }

  return (
    <Form onSubmit={onSubmit}>
      <BuyProcessFormUserTip>
        <div>
          <FormSectionInfo.InfoBoxTitled title="Kostnadssammanställning" />
          <Typography>
            Nedan visas en kostnadssammanställning för din beställning.
            Eventuella tillval kan ändras genom att gå tillbaka till tidigare
            steg och ändra valen där.
            <br />
            <br />
            Alla priser är angivna exklusive moms där inte annat anges. Av
            Bolagsverkets avgifter är 1.900 kr ej avdragsgilla.
          </Typography>
        </div>
      </BuyProcessFormUserTip>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Kostnader (ex. moms):">
          <Typography>
            Bolagsverkets avgifter:{' '}
            {renderMoney(extraServicesCost.totalPayment.registrationCost)}{' '}
            <br />
            Arvode och administrationskostnad för lagerbolag:{' '}
            {renderMoney(
              extraServicesCost.totalPayment.administrationCost,
            )}{' '}
            <br />
            {renderBeneficialCost(
              extraServicesCost.totalPayment.beneficialCost,
            )}
            {renderNameReviewCost(
              extraServicesCost.totalPayment.nameReviewCost,
            )}
            {renderTaxHelpCost(extraServicesCost.totalPayment.taxHelpCost)}
            <strong>
              Totalt att betala:{' '}
              {renderMoney(extraServicesCost.totalPayment.totalPayment)} (varav
              moms: {renderMoney(extraServicesCost.totalPayment.vat)})
            </strong>
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer className={classes.section}>
          <RHFRadioGroup
            className={classes.subSectionEnd}
            label="Hur vill du ta emot och signera handlingarna? *"
            control={formMethods.control}
            name={RECEIVE_OPTION}
            defaultValue="mail"
          >
            <RadioLabelled
              value="mail"
              label={CostStatementReceiveLabel.mail}
            />
            <RadioLabelled
              value="email"
              label={CostStatementReceiveLabel.email}
            />
          </RHFRadioGroup>

          <Collapse in={receiveByEmail}>
            <InputText
              ref={formMethods.register}
              errors={formMethods.errors}
              label="E-postadresser för underskrifter"
              name={RECEIVER_EMAILS}
              multiline
              rows={4}
              // eslint-disable-next-line max-len
              helperText="Det är viktigt att vi får e-postadresserna till alla som ska skriva under handlingarna digitalt eftersom avtalen skickas dit. Om du saknar adressen till någon går det bra att lämna raden tom, då kommer denna person att få logga in via en länk som vi mailar till huvudbeställaren."
            />
          </Collapse>
        </FormSectionContainer>

        <FormSectionContainer sectionTitle="">
          <RHFRadioGroup
            label="Faktura för kostnaderna ska ställas till"
            control={formMethods.control}
            errors={formMethods.errors}
            name="invoiceRecipient"
            required
          >
            <RadioLabelled
              value={`${InvoiceRecipient.Company}`}
              label="Nya bolaget"
            />
            <RadioLabelled
              value={`${InvoiceRecipient.Customer}`}
              label="Beställaren"
            />
          </RHFRadioGroup>
        </FormSectionContainer>
      </div>

      <ButtonGroup>
        <Button onClick={onBack}>Tillbaka</Button>
        <BuyProcessFormNextButton onClick={onForward}>
          Nästa
        </BuyProcessFormNextButton>
        <BuyProcessFormAdminSubmitButton loading={isUpdating}>
          Spara
        </BuyProcessFormAdminSubmitButton>
      </ButtonGroup>
    </Form>
  );
};
